import { FunnelIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';
import { useCallback, useMemo, useState } from 'react';
import {
  DropdownOption,
  DropdownOptionGroup,
  DropdownWithSearchInput,
} from '../../molecules/DropdownWithSearchInput/DropdownWithSearchInput';
import {
  AssetGroupIcon,
  CloudProviderIcon,
  getIntegrationProviderIcon,
  NonProdEnvironmentIcon,
  OmniFilterState,
  ProdEnvironmentIcon,
  useGetEnvironments,
  useListAssetGroups,
  useListCloudIntegrations,
  useOmniFilter,
} from '../../main';

import { useTranslation } from 'react-i18next';

export type OmniFilterIntegrationGroup = {
  groupId: string;
  name: string;
};

export const OmniFilter = ({
  integrationGroups,
}: {
  /**
   * The tenant's integration groups, supplied by the host because the
   * organism cannot depend on the api package. An absent or empty list
   * renders no Integration groups section.
   */
  integrationGroups?: OmniFilterIntegrationGroup[];
}) => {
  const { t } = useTranslation('designSystem');
  const [query, setQuery] = useState('');
  const { omniFilterState, setOmniFilterState, recentOmniFilters } =
    useOmniFilter();

  const { data: assetGroupsData, isLoading: isLoadingAssetGroups } =
    useListAssetGroups();

  const { data: environmentsData, isLoading: isLoadingEnvironments } =
    useGetEnvironments();

  const { data: integrationsData, isLoading: isLoadingIntegrations } =
    useListCloudIntegrations();

  const { selectedOption, icon } = useMemo<{
    selectedOption: DropdownOption | null;
    icon: any;
  }>(() => {
    if (omniFilterState.assetGroupIds?.length) {
      const selectedAssetGroupId = omniFilterState.assetGroupIds[0];
      const assetGroup = assetGroupsData?.find(
        ({ assetGroupId }) => assetGroupId === selectedAssetGroupId,
      );
      if (assetGroup) {
        const { assetGroupId, name } = assetGroup;
        return {
          icon: AssetGroupIcon,
          selectedOption: { id: assetGroupId, label: name },
        };
      }
    }

    if (omniFilterState.environmentIds?.length) {
      const selectedEnvironmentId = omniFilterState.environmentIds[0];
      const environment = environmentsData?.find(
        ({ assetGroupId }) => assetGroupId === selectedEnvironmentId,
      );
      if (environment) {
        const { assetGroupId, dimension, name } = environment;
        return {
          icon:
            dimension?.key === 'environment' &&
            dimension?.value === 'production'
              ? ProdEnvironmentIcon
              : NonProdEnvironmentIcon,
          selectedOption: { id: assetGroupId, label: name },
        };
      }
    }

    if (omniFilterState.integrationIds?.length) {
      const selectedIntegrationId = omniFilterState.integrationIds[0];
      const integration = integrationsData?.find(
        ({ integrationId }) => integrationId === selectedIntegrationId,
      );
      if (integration) {
        const { integrationId, provider, name } = integration;
        return {
          icon: getIntegrationProviderIcon(provider),
          selectedOption: { id: integrationId, label: name },
        };
      }
    }

    if (omniFilterState.integrationGroupIds?.length) {
      const selectedGroupId = omniFilterState.integrationGroupIds[0];
      const group = integrationGroups?.find(
        ({ groupId }) => groupId === selectedGroupId,
      );
      if (group) {
        return {
          icon: RectangleGroupIcon,
          selectedOption: { id: group.groupId, label: group.name },
        };
      }
    }

    return { selectedOption: null, icon: FunnelIcon };
  }, [
    omniFilterState,
    assetGroupsData,
    environmentsData,
    integrationsData,
    integrationGroups,
  ]);

  const filteredEnvironments = useMemo(() => {
    if (!environmentsData) {
      return [];
    }

    if (selectedOption) {
      return environmentsData;
    }

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return environmentsData;
    }
    return environmentsData.filter(({ name }) =>
      name.toLowerCase().includes(trimmedQuery.toLowerCase()),
    );
  }, [environmentsData, query, selectedOption]);

  const filteredIntegrations = useMemo(() => {
    if (!integrationsData) {
      return [];
    }

    if (selectedOption) {
      return integrationsData;
    }

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return integrationsData;
    }
    return integrationsData.filter((integration) =>
      integration.name.toLowerCase().includes(trimmedQuery.toLowerCase()),
    );
  }, [integrationsData, query, selectedOption]);

  const filteredAssetGroups = useMemo(() => {
    if (!assetGroupsData) {
      return [];
    }

    if (selectedOption) {
      return assetGroupsData;
    }

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return assetGroupsData;
    }
    return assetGroupsData.filter((assetGroup) =>
      assetGroup.name.toLowerCase().includes(trimmedQuery.toLowerCase()),
    );
  }, [assetGroupsData, query, selectedOption]);

  const filteredIntegrationGroups = useMemo(() => {
    if (!integrationGroups) {
      return [];
    }

    if (selectedOption) {
      return integrationGroups;
    }

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return integrationGroups;
    }
    return integrationGroups.filter(({ name }) =>
      name.toLowerCase().includes(trimmedQuery.toLowerCase()),
    );
  }, [integrationGroups, query, selectedOption]);

  const recentOptions: DropdownOption[] = useMemo(() => {
    const recent = recentOmniFilters.slice(0, 3);
    const options: DropdownOption[] = [];

    for (const recentFilter of recent) {
      const { filterId, filterType } = recentFilter;

      if (filterType === 'asset-group') {
        const assetGroup = assetGroupsData?.find(
          ({ assetGroupId }) => assetGroupId === filterId,
        );
        if (assetGroup) {
          options.push({
            id: assetGroup.assetGroupId,
            label: <AssetGroupOption name={assetGroup.name} />,
            queryString: assetGroup.name,
            isHighlighted: selectedOption?.id === assetGroup.assetGroupId,
          });
        }
      } else if (filterType === 'environment') {
        const environment = environmentsData?.find(
          ({ assetGroupId }) => assetGroupId === filterId,
        );
        if (environment) {
          const { assetGroupId, dimension, name } = environment;
          options.push({
            id: assetGroupId,
            label: <EnvironmentOption name={name} value={dimension?.value} />,
            queryString: name,
            isHighlighted: selectedOption?.id === assetGroupId,
          });
        }
      } else if (filterType === 'integration') {
        const integration = integrationsData?.find(
          ({ integrationId }) => integrationId === filterId,
        );
        if (integration) {
          const { integrationId, provider, name } = integration;
          options.push({
            id: integrationId,
            label: <IntegrationOption provider={provider} name={name} />,
            queryString: name,
            isHighlighted: selectedOption?.id === integrationId,
          });
        }
      } else if (filterType === 'integration-group') {
        const group = integrationGroups?.find(
          ({ groupId }) => groupId === filterId,
        );
        if (group) {
          options.push({
            id: group.groupId,
            label: <IntegrationGroupOption name={group.name} />,
            queryString: group.name,
            isHighlighted: selectedOption?.id === group.groupId,
          });
        }
      }
    }

    // Filter by query string if a query is provided
    const trimmedQuery = query.trim();
    if (trimmedQuery && !selectedOption) {
      const lowerQuery = trimmedQuery.toLowerCase();
      return options.filter(
        (option) =>
          option.queryString?.toLowerCase().includes(lowerQuery) ||
          option.id.toLowerCase().includes(lowerQuery),
      );
    }

    return options;
  }, [
    assetGroupsData,
    environmentsData,
    integrationsData,
    integrationGroups,
    recentOmniFilters,
    query,
    selectedOption,
  ]);

  const optionGroups: DropdownOptionGroup[] = useMemo(() => {
    const groups: DropdownOptionGroup[] = [];

    if (recentOptions.length > 0) {
      groups.push({
        name: t('recent'),
        options: recentOptions,
      });
    }

    if (filteredEnvironments.length > 0) {
      groups.push({
        name: t('environments'),
        options: filteredEnvironments.map(
          ({ assetGroupId, name, dimension }) => ({
            id: assetGroupId,
            label: <EnvironmentOption name={name} value={dimension?.value} />,
            queryString: name,
            isHighlighted: selectedOption?.id === assetGroupId,
          }),
        ),
      });
    }

    if (filteredIntegrationGroups.length > 0) {
      groups.push({
        name: t('integrationGroups'),
        options: filteredIntegrationGroups.map(({ groupId, name }) => ({
          id: groupId,
          label: <IntegrationGroupOption name={name} />,
          queryString: name,
          isHighlighted: selectedOption?.id === groupId,
        })),
      });
    }

    if (filteredIntegrations.length > 0) {
      groups.push({
        name: t('common:integrations'),
        options: filteredIntegrations.map(
          ({ integrationId, name, provider }) => ({
            id: integrationId,
            label: <IntegrationOption provider={provider} name={name} />,
            queryString: name,
            isHighlighted: selectedOption?.id === integrationId,
          }),
        ),
      });
    }

    if (filteredAssetGroups.length > 0) {
      groups.push({
        name: t('assetGroups'),
        options: filteredAssetGroups.map(({ assetGroupId, name }) => ({
          id: assetGroupId,
          label: <AssetGroupOption name={name} />,
          queryString: name,
          isHighlighted: selectedOption?.id === assetGroupId,
        })),
      });
    }

    return groups;
  }, [
    filteredAssetGroups,
    filteredEnvironments,
    filteredIntegrationGroups,
    filteredIntegrations,
    recentOptions,
    selectedOption,
    t,
  ]);

  const handleOptionSelect = useCallback(
    (option: DropdownOption) => {
      const queryString: string =
        typeof option.label === 'string' ? option.label : option.id;
      setQuery(queryString);

      // Determine which type this option is by checking all data sources
      const isAssetGroup = assetGroupsData?.some(
        ({ assetGroupId }) => assetGroupId === option.id,
      );
      const isEnvironment = environmentsData?.some(
        ({ assetGroupId }) => assetGroupId === option.id,
      );
      const isIntegration = integrationsData?.some(
        ({ integrationId }) => integrationId === option.id,
      );
      const isIntegrationGroup = integrationGroups?.some(
        ({ groupId }) => groupId === option.id,
      );

      const newState: OmniFilterState = {};

      if (isAssetGroup) {
        newState.assetGroupIds = [option.id];
      } else if (isEnvironment) {
        newState.environmentIds = [option.id];
      } else if (isIntegration) {
        newState.integrationIds = [option.id];
      } else if (isIntegrationGroup) {
        newState.integrationGroupIds = [option.id];
      }
      setOmniFilterState(newState);
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) {
        activeElement.blur();
      }
    },
    [
      assetGroupsData,
      environmentsData,
      integrationsData,
      integrationGroups,
      setOmniFilterState,
    ],
  );

  const queryValue = useMemo(() => {
    if (selectedOption) {
      if (typeof selectedOption.label === 'string') {
        return selectedOption.label;
      }
      if (selectedOption.queryString) {
        return selectedOption.queryString;
      }
      return selectedOption.id;
    }
    return query;
  }, [selectedOption, query]);

  // Truncate query and add an ellipsis when it is too long
  const MAX_VISIBLE_QUERY_LENGTH = 28;

  const queryString = useMemo(() => {
    if (selectedOption && queryValue.length > MAX_VISIBLE_QUERY_LENGTH) {
      return `${queryValue.substring(0, MAX_VISIBLE_QUERY_LENGTH - 1)}...`;
    }
    return queryValue;
  }, [selectedOption, queryValue]);

  const clearOmniFilter = useCallback(() => {
    setQuery('');
    setOmniFilterState({});
  }, [setOmniFilterState]);

  const isLoadingOptions =
    isLoadingAssetGroups || isLoadingEnvironments || isLoadingIntegrations;

  return (
    <div data-testid="omni-filter">
      <DropdownWithSearchInput
        icon={icon}
        isShowLimitedOptions
        isSingleSelection
        onClear={selectedOption ? clearOmniFilter : undefined}
        onOptionSelect={handleOptionSelect}
        onQueryChange={(newQuery) => {
          setQuery(newQuery);
          const currentSelection =
            typeof selectedOption?.label === 'string'
              ? selectedOption.label
              : selectedOption?.queryString || selectedOption?.id;
          if (newQuery !== currentSelection) {
            setOmniFilterState({});
          }
        }}
        optionGroups={optionGroups}
        placeholder={isLoadingOptions ? t('loadingEllipsis') : t('setGlobalFilter')}
        query={queryString}
      />
    </div>
  );
};

const EnvironmentOption = ({
  name,
  value,
}: {
  name: string;
  value: string | undefined;
}) => {
  return (
    <span className="flex flex-row items-center gap-2">
      {value === 'production' ? (
        <ProdEnvironmentIcon className="h-4 w-4" />
      ) : value === 'non-production' ? (
        <NonProdEnvironmentIcon className="h-4 w-4" />
      ) : (
        <></>
      )}
      {name}
    </span>
  );
};

const AssetGroupOption = ({ name }: { name: string }) => {
  return (
    <span className="flex flex-row items-center gap-2">
      <AssetGroupIcon className="h-4 w-4" />
      {name}
    </span>
  );
};

const IntegrationOption = ({
  provider,
  name,
}: {
  provider: string;
  name: string;
}) => (
  <span className="flex flex-row items-center gap-2">
    <CloudProviderIcon provider={provider} />
    {name}
  </span>
);

const IntegrationGroupOption = ({ name }: { name: string }) => (
  <span className="flex flex-row items-center gap-2">
    <RectangleGroupIcon className="h-4 w-4" />
    {name}
  </span>
);
